/**
 * Role & Permissions — ADMIN / SUPER_ADMIN only. Read-only view of the
 * signed-in admin's role and the effective permissions it grants (resolved
 * server-side via GET /auth/permissions, which applies the role + additional
 * − denied overlay). Super admins also get quick links to the security
 * surfaces they own.
 */

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { authAPI } from '../../services/api';
import { useUI } from '../../context/UIContext';
import { ROUTES } from '../../constants/constants';
import { ProfileCard, Pill, GhostButton } from './ProfilePrimitives';
import Icon from '../common/Icon';

const BORDER = '#ddd6c6';
const TEXT = '#08111f';
const MUTED = '#7a7060';

const ROLE_LABELS = {
  admin: 'Administrator',
  super_admin: 'Super Admin',
};

// Group a flat permission list by its `module` for a tidy, scannable layout.
function groupByModule(permissions) {
  const groups = {};
  permissions.forEach((p) => {
    const key = p.module || 'general';
    if (!groups[key]) groups[key] = [];
    groups[key].push(p);
  });
  return Object.entries(groups).sort((a, b) => a[0].localeCompare(b[0]));
}

export default function RolePermissionsSection({ user }) {
  const navigate = useNavigate();
  const { showToast } = useUI();
  const [permissions, setPermissions] = useState([]);
  const [roleName, setRoleName] = useState(user?.roleName || '');
  const [loading, setLoading] = useState(true);

  const isSuperAdmin = String(user?.roleName || '').toLowerCase() === 'super_admin';

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await authAPI.getPermissions();
        if (!active) return;
        setPermissions(res?.data?.permissions || []);
        setRoleName(res?.data?.roleName || user?.roleName || '');
      } catch (err) {
        if (active) showToast(err?.message || 'Could not load permissions', 'error');
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [showToast, user?.roleName]);

  const grouped = groupByModule(permissions);
  const roleLabel = ROLE_LABELS[String(roleName).toLowerCase()] || roleName || 'Administrator';

  return (
    <ProfileCard
      title="Role & Permissions"
      subtitle="What your administrator role grants. This view is read-only."
      icon="🔐"
      action={<Pill tone="gold">{roleLabel}</Pill>}
    >
      {loading ? (
        <div className="text-[12px]" style={{ color: MUTED }}>Loading permissions…</div>
      ) : permissions.length === 0 ? (
        <div className="text-[12px]" style={{ color: MUTED }}>
          No granular permissions are attached to this role.
        </div>
      ) : (
        <div className="space-y-4">
          {grouped.map(([module, perms]) => (
            <div key={module}>
              <div className="text-[10.5px] uppercase tracking-[1px] font-semibold mb-2" style={{ color: MUTED }}>
                {module}
              </div>
              <div className="flex flex-wrap gap-1.5">
                {perms.map((p) => (
                  <span
                    key={p.name}
                    className="text-[11px] font-medium px-2 py-[3px] rounded-md border"
                    style={{ borderColor: BORDER, color: TEXT, background: '#f8f5ef' }}
                    title={p.description || p.name}
                  >
                    {p.displayName || p.action || p.name}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {isSuperAdmin && (
        <div className="mt-5 pt-4 border-t flex items-center gap-2 flex-wrap" style={{ borderColor: '#eee6d8' }}>
          <span className="text-[11px] font-semibold mr-1" style={{ color: MUTED }}>Super admin tools:</span>
          <GhostButton onClick={() => navigate(ROUTES.ADMIN_FLAG_INBOX)}>
            <span className="inline-flex items-center gap-[5px]"><Icon name="🚩" size={12} />Flag Inbox</span>
          </GhostButton>
          <GhostButton onClick={() => navigate(ROUTES.ADMIN_AUDIT_LOGS)}>
            <span className="inline-flex items-center gap-[5px]"><Icon name="shield" size={12} />Audit Logs</span>
          </GhostButton>
          <GhostButton onClick={() => navigate(ROUTES.ADMIN_ROLES)}>Roles & Permissions</GhostButton>
        </div>
      )}
    </ProfileCard>
  );
}
